import {
  BrandSubscriptionPaymentFrequency,
  BrandSubscriptionPlatform,
  BrandSubscriptionTier,
  NotOfferedBrandSubscriptionPlatform,
} from '@/utils/brand-subscription/constants'
import {BrandMonitoringTierContentType} from '@/utils/brand-subscription/types'

export const brandMonitoringPlatforms: Record<
  BrandSubscriptionPlatform,
  {
    title: string
    imageSrc: string
    imageAlt: string
    priceNote?: string
    submenu?: {
      title: string
      mobileTitle?: string
      noResultsDescription?: string
    }
    editButtonText?: {
      single: string
      multiple: string
    }
    slug?: string
  }
> = {
  [BrandSubscriptionPlatform.amazon]: {
    title: 'amazon-title',
    imageSrc: '/images/platforms/amazon.svg',
    imageAlt: 'Amazon',
    priceNote: 'price-for-domain',
    submenu: {
      title: 'brand-monitoring.amazon-domain-selection-title',
      mobileTitle: 'brand-monitoring.amazon-domain-selection-mobile-title',
    },
    editButtonText: {
      single: 'one-selected-domain-title',
      multiple: 'num-of-selected-domains-title',
    },
    slug: 'amazon',
  },
  [BrandSubscriptionPlatform.facebook]: {
    title: 'facebook-title',
    imageSrc: '/images/platforms/facebook.svg',
    imageAlt: 'Facebook',
    priceNote: 'month-text',
    slug: 'facebook',
  },
  [BrandSubscriptionPlatform.ebay]: {
    title: 'ebay-title',
    imageSrc: '/images/platforms/ebay.svg',
    imageAlt: 'eBay',
    priceNote: 'month-text',
    slug: 'ebay',
  },
  [BrandSubscriptionPlatform.instagram]: {
    title: 'instagram-title',
    imageSrc: '/images/platforms/instagram.svg',
    imageAlt: 'Instagram',
    priceNote: 'month-text',
    slug: 'instagram',
  },
  [BrandSubscriptionPlatform.googleAds]: {
    title: 'google-ads-title',
    imageSrc: '/images/platforms/google-ads.svg',
    imageAlt: 'Google Ads',
    priceNote: 'price-for-country',
    submenu: {
      title: 'brand-monitoring.google-ads-country-selection-title',
      mobileTitle: 'brand-monitoring.google-ads-country-selection-mobile-title',
      noResultsDescription: 'brand-monitoring.google-ads-country-no-results-desc',
    },
    editButtonText: {
      single: 'one-selected-country-title',
      multiple: 'num-of-selected-countries-title',
    },
    slug: 'google-ads',
  },
  [BrandSubscriptionPlatform.webDomains]: {
    title: 'web-domains-title',
    imageSrc: '/images/platforms/web-domains.svg',
    imageAlt: 'Web domains',
    priceNote: 'month-text',
    slug: 'web-domains',
  },
  [BrandSubscriptionPlatform.trademarkRegisters]: {
    title: 'trademark-registers-title',
    imageSrc: '/images/platforms/trademark-registers.svg',
    imageAlt: 'Trademark registers',
    priceNote: 'month-text',
    slug: 'trademark-watch',
  },
  [BrandSubscriptionPlatform.businessRegisters]: {
    title: 'business-registers-title',
    imageSrc: '/images/platforms/business-registers.svg',
    imageAlt: 'Business registers',
    priceNote: 'month-text',
    slug: 'business-registers',
  },
  [BrandSubscriptionPlatform.appStore]: {
    title: 'app-store-title',
    imageSrc: '/images/platforms/app-store.svg',
    imageAlt: 'App Store',
    priceNote: 'month-text',
  },
  [BrandSubscriptionPlatform.googlePlay]: {
    title: 'google-play-title',
    imageSrc: '/images/platforms/google-play.svg',
    imageAlt: 'Google Play',
    priceNote: 'month-text',
  },
  [BrandSubscriptionPlatform.aliExpress]: {
    title: 'aliexpress-title',
    imageSrc: '/images/platforms/aliexpress.svg',
    imageAlt: 'AliExpress',
    priceNote: 'month-text',
  },
  [BrandSubscriptionPlatform.youtube]: {
    title: 'youtube-title',
    imageSrc: '/images/platforms/youtube.svg',
    imageAlt: 'Youtube',
    priceNote: 'month-text',
  },
  [BrandSubscriptionPlatform.etsy]: {
    title: 'etsy-title',
    imageSrc: '/images/platforms/etsy.svg',
    imageAlt: 'Etsy',
    priceNote: 'month-text',
  },
  [BrandSubscriptionPlatform.tiktok]: {
    title: 'tiktok-title',
    imageSrc: '/images/platforms/tiktok.svg',
    imageAlt: 'Tiktok',
  },
  [BrandSubscriptionPlatform.temu]: {
    title: 'temu-title',
    imageSrc: '/images/platforms/temu.svg',
    imageAlt: 'Temu',
  },
  [BrandSubscriptionPlatform.fruugo]: {
    title: 'fruugo-title',
    imageSrc: '/images/platforms/fruugo.svg',
    imageAlt: 'Fruugo',
  },
  [BrandSubscriptionPlatform.shopify]: {
    title: 'shopify-title',
    imageSrc: '/images/platforms/shopify.svg',
    imageAlt: 'Shopify',
  },
  [BrandSubscriptionPlatform.walmart]: {
    title: 'walmart-title',
    imageSrc: '/images/platforms/walmart.svg',
    imageAlt: 'Walmart',
  },
}

type PlatformImage = {imageSrc: string; imageAlt: string}

// Icons of every platform, including ones we don't monitor but have a brand protection guide for.
export const brandMonitoringPlatformImages: Record<
  BrandSubscriptionPlatform | NotOfferedBrandSubscriptionPlatform,
  PlatformImage
> = {
  ...(Object.fromEntries(
    Object.entries(brandMonitoringPlatforms).map(([platform, {imageSrc, imageAlt}]) => [
      platform,
      {imageSrc, imageAlt},
    ]),
  ) as Record<BrandSubscriptionPlatform, PlatformImage>),
  [NotOfferedBrandSubscriptionPlatform.twitterX]: {
    imageSrc: '/images/platforms/x.svg',
    imageAlt: 'X',
  },
}

export const brandMonitoringPaymentFrequency: Record<
  BrandSubscriptionPaymentFrequency,
  {number: number; unit: string; label?: string}
> = {
  [BrandSubscriptionPaymentFrequency.yearly]: {
    number: 12,
    unit: 'months-text',
    label: 'brand-monitoring.save-percentage-title',
  },
  [BrandSubscriptionPaymentFrequency.monthly]: {
    number: 1,
    unit: 'month-text',
  },
}

const essentialTierItems = [
  {
    title: 'brand-monitoring.tier-essential-item-0-title',
    tooltipText: 'brand-monitoring.tier-essential-item-0-tooltip',
  },
  {
    title: 'brand-monitoring.tier-essential-item-1-title',
    tooltipText: 'brand-monitoring.tier-essential-item-1-tooltip',
  },
]

const advancedTierItems = [
  {
    title: 'brand-monitoring.tier-advanced-item-1-title',
    tooltipText: 'brand-monitoring.tier-advanced-item-1-tooltip',
  },
  {
    title: 'brand-monitoring.tier-advanced-item-2-title',
    tooltipText: 'brand-monitoring.tier-advanced-item-2-tooltip',
  },
  {
    title: 'brand-monitoring.tier-advanced-item-3-title',
    tooltipText: 'brand-monitoring.tier-advanced-item-3-tooltip',
  },
  {
    title: 'brand-monitoring.tier-advanced-item-4-title',
    tooltipText: 'brand-monitoring.tier-advanced-item-4-tooltip',
  },
]

const advancedEnforcementTierItems = [
  {title: 'brand-monitoring.tier-advanced-enforcement-item-0-title'},
  ...essentialTierItems,
  ...advancedTierItems,
]

export const brandMonitoringTiers: Record<string, BrandMonitoringTierContentType> = {
  [BrandSubscriptionTier.v2Essential]: {
    title: 'brand-monitoring.tier-essential-title',
    subtitle: 'brand-monitoring.tier-essential-subtitle',
    priceNote: 'month-text',
    pricingTitle: 'essential-monitoring-plan-title',
    items: {summary: essentialTierItems, all: essentialTierItems},
  },
  [BrandSubscriptionTier.v2Advanced]: {
    title: 'brand-monitoring.tier-advanced-title',
    subtitle: 'brand-monitoring.tier-advanced-subtitle',
    priceNote: 'month-text',
    pricingTitle: 'advanced-monitoring-plan-title',
    items: {
      summary: [{title: 'brand-monitoring.tier-advanced-item-0-title'}, ...advancedTierItems],
      all: [...essentialTierItems, ...advancedTierItems],
    },
  },
  [BrandSubscriptionTier.v2AdvancedEnforcement]: {
    title: 'brand-monitoring.tier-advanced-enforcement-title',
    priceNote: 'month-text',
    items: {summary: advancedEnforcementTierItems, all: advancedEnforcementTierItems},
  },
}
